<script>
import TeletelSlaAPI from 'dashboard/api/teletel/sla';
import WootLabel from 'dashboard/components-next/label/Label.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import SLAReportFilters from 'dashboard/routes/dashboard/settings/reports/components/SLA/SLAReportFilters.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

// Read-only SLA compliance report. Breach notifications are generated by the
// backend event path; this screen only reads and exports recorded data. The
// endpoint remains fail-closed while TELETEL_SLA_ENABLED is not explicitly
// true.
export default {
  components: {
    WootLabel,
    BaseTable,
    BaseTableRow,
    BaseTableCell,
    SLAReportFilters,
    NextButton,
  },
  data() {
    return {
      isLoading: false,
      isDisabled: false,
      hasError: false,
      summary: {},
      rows: [],
      meta: {},
      filters: {},
      page: 1,
    };
  },
  computed: {
    tableHeaders() {
      return [
        this.$t('SLA.TELETEL.REPORT.TABLE_HEADER.POLICY'),
        this.$t('SLA.TELETEL.REPORT.TABLE_HEADER.CONVERSATION'),
        this.$t('SLA.TELETEL.REPORT.TABLE_HEADER.INBOX'),
        this.$t('SLA.TELETEL.REPORT.TABLE_HEADER.STATUS'),
        this.$t('SLA.TELETEL.REPORT.TABLE_HEADER.RECORDED_AT'),
        this.$t('SLA.TELETEL.REPORT.TABLE_HEADER.EVENTS'),
      ];
    },
    statusLabels() {
      return {
        active: this.$t('SLA.TELETEL.REPORT.STATUS.ACTIVE'),
        hit: this.$t('SLA.TELETEL.REPORT.STATUS.HIT'),
        missed: this.$t('SLA.TELETEL.REPORT.STATUS.MISSED'),
        active_with_misses: this.$t(
          'SLA.TELETEL.REPORT.STATUS.ACTIVE_WITH_MISSES'
        ),
      };
    },
    eventTypeLabels() {
      return {
        frt: this.$t('SLA.TELETEL.REPORT.EVENT_TYPES.FRT'),
        nrt: this.$t('SLA.TELETEL.REPORT.EVENT_TYPES.NRT'),
        rt: this.$t('SLA.TELETEL.REPORT.EVENT_TYPES.RT'),
      };
    },
    statusSummary() {
      return Object.entries(this.summary.by_status || {}).map(
        ([status, count]) => ({
          status,
          count,
          label: this.statusLabel(status),
        })
      );
    },
  },
  mounted() {
    this.fetchReport();
  },
  methods: {
    async fetchReport() {
      this.isLoading = true;
      try {
        const { data } = await TeletelSlaAPI.getReport({
          ...this.filters,
          page: this.page,
        });
        this.summary = data.summary || {};
        this.rows = data.payload || [];
        this.meta = data.meta || {};
        this.isDisabled = false;
        this.hasError = false;
      } catch (error) {
        if (error?.response?.status === 403) {
          this.isDisabled = true;
        } else {
          this.hasError = true;
        }
      } finally {
        this.isLoading = false;
      }
    },
    statusLabel(status) {
      return this.statusLabels[status] || status;
    },
    statusColor(status) {
      return (
        { hit: 'teal', missed: 'ruby', active_with_misses: 'amber' }[status] ||
        'slate'
      );
    },
    displayEvents(events) {
      if (!events.length) return '-';
      return events
        .map(
          event => this.eventTypeLabels[event.event_type] || event.event_type
        )
        .join(', ');
    },
    displayConversation(displayId) {
      return displayId ? `#${displayId}` : '-';
    },
    displayTimestamp(timestamp) {
      return messageTimestamp(timestamp, 'LLL d yyyy, h:mm a');
    },
    onFilterChange(filters) {
      this.filters = {
        ...filters,
        label: filters.label_list,
      };
      delete this.filters.label_list;
      this.page = 1;
      this.fetchReport();
    },
    changePage(page) {
      if (page < 1) return;
      this.page = page;
      this.fetchReport();
    },
    async downloadReport() {
      const { data } = await TeletelSlaAPI.downloadReport(this.filters);
      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'teletel-sla-breaches.csv';
      link.click();
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<template>
  <section class="contents">
    <div
      v-if="!isDisabled && !hasError && !isLoading"
      class="flex flex-col gap-4 mt-8"
    >
      <div class="flex flex-col gap-1">
        <h3 class="text-heading-2 text-n-slate-12">
          {{ $t('SLA.TELETEL.REPORT.TITLE') }}
        </h3>
        <p class="text-body-main text-n-slate-11">
          {{ $t('SLA.TELETEL.REPORT.DESCRIPTION') }}
        </p>
      </div>
      <div class="flex flex-wrap items-center justify-between gap-3">
        <SLAReportFilters @filter-change="onFilterChange" />
        <NextButton
          :label="$t('SLA.TELETEL.REPORT.DOWNLOAD')"
          icon="i-lucide-download"
          size="sm"
          @click="downloadReport"
        />
      </div>
      <div class="flex flex-wrap items-center gap-2">
        <WootLabel
          :label="`${$t('SLA.TELETEL.REPORT.TOTAL')}: ${summary.total || 0}`"
          color="slate"
          compact
        />
        <WootLabel
          :label="`${$t('SLA.TELETEL.REPORT.HIT_RATE')}: ${summary.hit_rate ?? '-'}%`"
          color="teal"
          compact
        />
        <WootLabel
          v-for="entry in statusSummary"
          :key="entry.status"
          :label="`${entry.label}: ${entry.count}`"
          :color="statusColor(entry.status)"
          compact
        />
        <WootLabel
          :label="
            summary.runtime_enabled
              ? $t('SLA.TELETEL.REPORT.RUNTIME_ENABLED')
              : $t('SLA.TELETEL.REPORT.RUNTIME_DISABLED')
          "
          :color="summary.runtime_enabled ? 'teal' : 'slate'"
          compact
        />
      </div>
      <p v-if="!rows.length" class="text-body-main text-n-slate-11">
        {{ $t('SLA.TELETEL.REPORT.EMPTY') }}
      </p>
      <BaseTable v-else :headers="tableHeaders" :items="rows">
        <template #row="{ items }">
          <BaseTableRow v-for="row in items" :key="row.id" :item="row">
            <template #default>
              <BaseTableCell>
                <span class="text-body-main text-n-slate-12 truncate">
                  {{ row.policy.name }}
                </span>
              </BaseTableCell>
              <BaseTableCell class="w-28">
                <span class="text-body-main text-n-slate-12">
                  {{ displayConversation(row.conversation_display_id) }}
                </span>
              </BaseTableCell>
              <BaseTableCell>
                <span class="text-body-main text-n-slate-11 truncate">
                  {{ row.inbox_name || '-' }}
                </span>
              </BaseTableCell>
              <BaseTableCell class="w-36">
                <WootLabel
                  :label="statusLabel(row.sla_status)"
                  :color="statusColor(row.sla_status)"
                  compact
                />
              </BaseTableCell>
              <BaseTableCell class="w-44">
                <span class="text-body-main text-n-slate-11">
                  {{ displayTimestamp(row.created_at) }}
                </span>
              </BaseTableCell>
              <BaseTableCell>
                <span class="text-body-main text-n-slate-11">
                  {{ displayEvents(row.events) }}
                </span>
              </BaseTableCell>
            </template>
          </BaseTableRow>
        </template>
      </BaseTable>
      <div
        v-if="meta.total_count > meta.per_page"
        class="flex justify-end gap-2"
      >
        <NextButton
          :label="$t('SLA.TELETEL.REPORT.PREVIOUS')"
          size="sm"
          ghost
          :disabled="page <= 1"
          @click="changePage(page - 1)"
        />
        <NextButton
          :label="$t('SLA.TELETEL.REPORT.NEXT')"
          size="sm"
          ghost
          :disabled="page * meta.per_page >= meta.total_count"
          @click="changePage(page + 1)"
        />
      </div>
    </div>
  </section>
</template>
